import { useCallback } from 'react'

import { AsyncSelect, Badge, Select, Space, SubText } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { expenseRepository } from '@webapp/modules/expense/data-access/repositories/ExpenseRepository'
import { travelRepository } from '@webapp/modules/expense/data-access/repositories/TravelRepository'
import { WatchlistApprover } from '@webapp/modules/expense/entities/WatchlistApprover'
import messages from './messages'

export interface IWatchlistApproverSelectProps {
  onChange: (approverId: Nullable<number>) => void
  /** Which aggregate's approval watchlist to source (defaults to expense). */
  objectType?: 'expense' | 'travel'
}

export const WatchlistApproverSelect = ({
  onChange,
  objectType = 'expense',
}: IWatchlistApproverSelectProps) => {
  const intl = useProcurifyIntl()

  const handleChange = (option: WatchlistApprover | null) => {
    onChange(option ? option.id : null)
  }

  const loadOptions = useCallback(
    async (
      inputValue: string,
      pagination: { page: number },
      callback: (result: {
        options: WatchlistApprover[]
        pagination: { hasMore: boolean; page: number }
      }) => void
    ) => {
      const repository =
        objectType === 'travel' ? travelRepository : expenseRepository

      try {
        const result = await repository.listWatchlistApprovers({
          search: inputValue || undefined,
          page: pagination.page,
          pageSize: 20,
        })
        const hasMore = result.pagination.page < result.pagination.numPages
        callback({
          options: result.data.map((d) => new WatchlistApprover(d)),
          pagination: { hasMore, page: pagination.page + 1 },
        })
      } catch {
        callback({
          options: [],
          pagination: { hasMore: false, page: 1 },
        })
      }
    },
    [objectType]
  )

  const formatOptionLabel = (option: WatchlistApprover) => {
    return (
      <Space
        direction='horizontal'
        justify='space-between'
        align='center'
        size='small'
      >
        <SubText>{option.fullName}</SubText>
        {option.hasApprovalCount && <Badge>{option.approvalCount}</Badge>}
      </Space>
    )
  }

  return (
    <AsyncSelect<WatchlistApprover>
      placeholder={intl.formatMessage(messages.placeholder)}
      isClearable
      loadOptions={loadOptions}
      getOptionLabel={(option) =>
        option.hasApprovalCount
          ? `${option.fullName} (${option.approvalCount})`
          : option.fullName
      }
      stickies={({ selectOption }) => [
        <Select.StickyOption
          key={0}
          onClick={() =>
            selectOption({
              id: -1,
              fullName: intl.formatMessage(messages.allLabel),
            })
          }
        >
          {intl.formatMessage(messages.allLabel)}
        </Select.StickyOption>,
      ]}
      getOptionValue={(option) => String(option.id)}
      formatOptionLabel={formatOptionLabel}
      onChange={handleChange}
      menuPortalTarget={document.body}
    />
  )
}

WatchlistApproverSelect.displayName = 'WatchlistApproverSelect'
